import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { tap } from '../../lib/idempotency';
import { IconStop, IconTimer } from '../icons';

/**
 * A running clock has to be visible from anywhere, or people forget it and bill a
 * Tuesday to one job. It lives in the shell for exactly that reason, ticks in real time,
 * links to the job it's on, and can be stopped without navigating.
 */
function elapsed(since: string | Date): string {
  const ms = Date.now() - new Date(since).getTime();
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function RunningTimerChip({ onNavigate }: { onNavigate?: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const running = useQuery(trpc.costing.myRunningTimer.queryOptions());
  const stop = useMutation(
    trpc.costing.stopTimer.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  const [, tick] = useState(0);

  const entry = running.data?.entry;
  useEffect(() => {
    if (!entry) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [entry]);

  // No clock running (or we can't tell yet) — render nothing rather than a hopeful zero.
  if (!running.data) return null;

  return (
    <>
      {/* Collapsed-to-icon-rail rendering: the clock has to be visible from ANYWHERE —
          that is this component's founding invariant — so collapsing the sidebar swaps
          the pill for a compact glyph instead of hiding a running billing timer. CSS
          only: the group-data classes are inert outside the sidebar. */}
      <Link
        to="/jobs/$jobId"
        params={{ jobId: running.data.job.id }}
        onClick={onNavigate}
        aria-label={`Timer running on #${running.data.job.jobNumber} — open the job`}
        title={`Timer running on #${running.data.job.jobNumber}`}
        className="mb-2 hidden size-8 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--sw-brand-text)]/40 bg-[var(--sw-brand-pill)] text-[var(--sw-brand-text)] group-data-[collapsible=icon]:flex"
      >
        <IconTimer size={15} />
      </Link>
      <div className="mb-2 rounded-[var(--radius-sm)] border border-[var(--sw-brand-text)]/40 bg-[var(--sw-brand-pill)] px-2 py-1.5 group-data-[collapsible=icon]:hidden">
        <div className="flex items-center gap-2">
          <IconTimer size={15} className="shrink-0 text-[var(--sw-brand-text)]" />
          <span
            className="font-mono text-sm font-semibold tabular-nums text-[var(--sw-brand-text)]"
            aria-live="off"
          >
            {elapsed(running.data.entry.startedAt)}
          </span>
          <button
            type="button"
            onClick={() => stop.mutate({ timeEntryId: running.data!.entry.id, ...tap() })}
            disabled={stop.isPending}
            aria-label="Stop timer"
            title="Stop timer"
            className="ml-auto rounded-[var(--radius-sm)] p-1 text-[var(--sw-brand-text)] hover:bg-surface"
          >
            <IconStop size={14} />
          </button>
        </div>
        <Link
          to="/jobs/$jobId"
          params={{ jobId: running.data.job.id }}
          onClick={onNavigate}
          className="mt-0.5 block truncate text-[11px] text-muted-foreground hover:underline"
        >
          #{running.data.job.jobNumber} · {running.data.job.title}
        </Link>
      </div>
    </>
  );
}
